import { createContext, useContext, useEffect, useState } from "react";
import { routes } from "../utils/constants";
import diseaseApi from "../apis/disease.api";
import { useAppState } from "./app-state";
import { authenticateApp } from "./auth-context";

interface IPollingContext {
  // polling
  startPollingForAnalysis: (userId: string, analysisType: string) => void;
  activePolls: string[];
  isPolling: boolean;
  removePollingRequest: (analysisType: string) => void;
  onShowSnackbarWithAction: (
    message: string,
    variant: "success" | "error",
    actionLink: string
  ) => void;
  onHideSnackbar: () => void;
  pollingQueue: string[];
  setPollingQueue: React.Dispatch<React.SetStateAction<string[]>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  reportDetails: any;
  setReportDetails: React.Dispatch<React.SetStateAction<any>>;
  pastReportsList: any;
  setPastReportsList: React.Dispatch<React.SetStateAction<any>>;
  getPreviousBloodReports: () => void;
  getReportDetails: (reportId: number) => void;
  deleteReport: (reportName: number) => void;
  getPreviousImageReport: () => Promise<void>;
  deletePreviousImageReport: (reportName: number) => Promise<void>;
  imageReportsList: any;
  setImageReportsList: React.Dispatch<React.SetStateAction<any>>;
  reportTrends: any;
  setReportTrends: React.Dispatch<React.SetStateAction<any>>;
}

export const pollingContext = createContext<IPollingContext>(
  {} as IPollingContext
);

const usePollingStateProvider = () => {
  const { onShowSnackbarWithAction, onHideSnackbar, currentClinic } =
    useAppState();

  const { clinicUserId } = authenticateApp();

  // polling
  const [pollingQueue, setPollingQueue] = useState<string[]>([]); // ✅ Queue for polling requests
  const [pollingResults, setPollingResults] = useState<{ [key: string]: any }>(
    {}
  ); // ✅ Stores analysis results
  const [activePolls, setActivePolls] = useState<string[]>([]); // ✅ Active polling requests
  const [isPolling, setIsPolling] = useState<boolean>(false); // ✅ Global polling state

  // loader for loading screen - 1. loading prev files, 2. Loading the details of that report(Tongue/ Blood, etc)
  const [loading, setLoading] = useState<boolean>(false);
  const [reportDetails, setReportDetails] = useState<any>(null);
  const [pastReportsList, setPastReportsList] = useState<any>(null);
  const [imageReportsList, setImageReportsList] = useState<any>(null);
  const [reportTrends, setReportTrends] = useState<any>(null);

  // ✅ Adds a new analysis type to the polling queue
  const startPollingForAnalysis = (userId: string, analysisType: string) => {
    setPollingQueue((prevQueue) => {
      if (!prevQueue.includes(analysisType)) {
        return [...prevQueue, analysisType]; // Add to queue if not already present
      }
      return prevQueue;
    });
  };

  // ✅ Removes a completed polling request from the queue
  const removePollingRequest = (analysisType: string) => {
    setPollingQueue((prevQueue) =>
      prevQueue.filter((type) => type !== analysisType)
    );
    setActivePolls((prevPolls) =>
      prevPolls.filter((type) => type !== analysisType)
    );
  };

  // ✅ Executes polling in sequence without blocking the UI
  useEffect(() => {
    if (!clinicUserId || !currentClinic) return; // Ensure clinicUserId and currentClinic are available
    if (pollingQueue.length === 0 || isPolling) return; // No pending requests OR already polling

    let currentType = pollingQueue[0]; // Get first request from queue
    setIsPolling(true);
    setActivePolls((prev) => [...prev, currentType]);

    const pollInterval = 2000; // Poll every 2 seconds
    const maxAttempts = 15;
    let attempts = 0;

    const interval = setInterval(async () => {
      try {
        attempts++;
        const result = await diseaseApi.getAllDisease(clinicUserId!);

        if (result && result.risks) {
          setPollingResults((prev) => ({
            ...prev,
            [currentType]: result.data,
          })); // ✅ Store result
          clearInterval(interval);
          removePollingRequest(currentType);

          // ✅ Show snackbar message instead of blocking UI
          onShowSnackbarWithAction(
            `View the result of ${currentType} Analysis here`,
            "success",
            currentType === "tongue"
              ? `${routes.ScoreResult}/Tongue Analysis`
              : `${routes.LabReports}/${clinicUserId}`,
            30000
          );
        }

        if (attempts >= maxAttempts) {
          console.error(`Polling timed out for ${currentType}.`);
          clearInterval(interval);
          removePollingRequest(currentType);
        }
      } catch (error) {
        console.error(`Error polling for ${currentType}:`, error);
      }
    }, pollInterval);
  }, [pollingQueue, isPolling, clinicUserId, currentClinic]);

  const getPreviousBloodReports = async () => {
    try {
      setLoading(true);

      // Simulating an API delay of 3 seconds
      setTimeout(() => {
        const dummyReports = [
          { id: 1, name: "Blood_Report_Jan.pdf" },
          { id: 2, name: "Blood_Report_Feb.pdf" },
          { id: 3, name: "Blood_Report_Mar.pdf" },
        ];

        setPastReportsList(dummyReports); // Save the dummy list
        setLoading(false);
      }, 3000); // 3-second delay
    } catch (error) {
      console.error("Error fetching previous blood reports:", error);
    } finally {
      // Ensures loading state resets even if an error occurs
      setTimeout(() => setLoading(false), 3000);
    }
  };

  const deleteReport = async (reportName: number) => {
    try {
      // 1. Set loading to true
      setLoading(true);

      // 3. Simulate API delay using setTimeout (3 seconds)
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // 2. Remove the report from the list immediately (optimistic update)
      setPastReportsList((prevReports: any) => {
        return prevReports.filter((report: any) => report.name !== reportName);
      });
    } catch (error) {
      console.error("Error deleting report:", error);
    } finally {
      setLoading(false); // Reset loading state
    }
  };

  const getPreviousImageReport = async () => {
    try {
      setLoading(true);

      // Simulating an API delay of 3 seconds
      setTimeout(() => {
        const dummyReports = [
          { id: 1, name: "Image_Report_Jan.pdf" },
          { id: 2, name: "Image_Report_Feb.pdf" },
          { id: 3, name: "Image_Report_Mar.pdf" },
        ];

        setImageReportsList(dummyReports); // Save the dummy list
        setLoading(false);
      }, 3000); // 3-second delay
    } catch (error) {
      console.error("Error fetching previous blood reports:", error);
    } finally {
      // Ensures loading state resets even if an error occurs
      setTimeout(() => setLoading(false), 3000);
    }
  };

  const deletePreviousImageReport = async (reportName: number) => {
    try {
      // 1. Set loading to true
      setLoading(true);

      // 3. Simulate API delay using setTimeout (3 seconds)
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // 2. Remove the report from the list immediately (optimistic update)
      setImageReportsList((prevReports: any) => {
        return prevReports.filter((report: any) => report.name !== reportName);
      });
    } catch (error) {
      console.error("Error deleting report:", error);
    } finally {
      setLoading(false); // Reset loading state
    }
  };

  const getReportDetails = async (reportId: number) => {
    try {
      setPastReportsList(null); // Reset past reports list

      // Simulate a 3-second delay
      setTimeout(() => {
        const dummyData = [
          {
            title: "Complete Blood Count (CBC)",
            date: "2021-01-01",
            bloodParametersCount: 2,
            bloodParameters: [
              {
                name: "Glucose Fasting Plasma",
                biomarkers: 1,
                biomarkerDetails: [
                  {
                    name: "Lymphocytes",
                    value: 0.2,
                    unit: "10^9/L",
                    referenceRange: {
                      min: 0.8,
                      max: 4.0,
                    },
                  },
                ],
              },
              {
                name: "Lipid Screen Serum",
                biomarkers: 2,
                biomarkerDetails: [
                  {
                    name: "Lymphocytes",
                    value: 0.2,
                    unit: "10^9/L",
                    referenceRange: {
                      min: 0.8,
                      max: 4.0,
                    },
                  },
                  {
                    name: "Lymphocytes",
                    value: 0.2,
                    unit: "10^9/L",
                    referenceRange: {
                      min: 0.8,
                      max: 4.0,
                    },
                  },
                ],
              },
            ],
          },
          {
            title: "Complete Blood Count (CBC)",
            date: "2022-07-01",
            bloodParametersCount: 2,
            bloodParameters: [
              {
                name: "Glucose Fasting Plasma",
                biomarkers: 1,
                biomarkerDetails: [
                  {
                    name: "Lymphocytes",
                    value: 0.2,
                    unit: "10^9/L",
                    referenceRange: {
                      min: 0.8,
                      max: 4.0,
                    },
                  },
                ],
              },
              {
                name: "Lipid Screen Serum",
                biomarkers: 2,
                biomarkerDetails: [
                  {
                    name: "Lymphocytes",
                    value: 0.2,
                    unit: "10^9/L",
                    referenceRange: {
                      min: 0.8,
                      max: 4.0,
                    },
                  },
                  {
                    name: "Lymphocytes",
                    value: 0.2,
                    unit: "10^9/L",
                    referenceRange: {
                      min: 0.8,
                      max: 4.0,
                    },
                  },
                ],
              },
            ],
          },
        ];

        setPastReportsList(dummyData); // Set the dummy data
      }, 3000); // 3-second delay
    } catch (error) {
      console.error("Error fetching report details:", error);
    }
  };

  return {
    // polling
    startPollingForAnalysis,
    activePolls,
    isPolling,
    removePollingRequest,
    onShowSnackbarWithAction,
    onHideSnackbar,
    pollingQueue,
    setPollingQueue,
    loading,
    setLoading,
    reportDetails,
    setReportDetails,
    pastReportsList,
    setPastReportsList,
    getPreviousBloodReports,
    getReportDetails,
    deleteReport,
    deletePreviousImageReport,
    getPreviousImageReport,
    imageReportsList,
    setImageReportsList,
    reportTrends,
    setReportTrends,
  };
};

interface Props {
  children: React.ReactNode;
}

export const PollingStateProvider = ({ children }: Props) => {
  const app = usePollingStateProvider();

  return (
    <pollingContext.Provider value={app}>{children}</pollingContext.Provider>
  );
};

export const usePollingState = () => {
  return useContext(pollingContext);
};
